import { useQuery } from '@tanstack/react-query'
import { useEffect, useState, type InputHTMLAttributes } from 'react'

import { fetchAbnDetails } from '@/app/actions/abn.actions'
import { Input } from '@/components/primitives/Input/Input'
import { Loader } from '@/components/primitives/Loader/Loader'
import { cn } from '@/lib/utils'

type ABNInputProps = InputHTMLAttributes<HTMLInputElement> & {
  defaultValue?: string
  onInputChange?: (value: string) => void
  onAbnSubmit?: ({ abn, isValidABN, error }: { abn: string; isValidABN?: boolean; error?: string | null }) => void
  inputError?: string | null
}

type AbnLookupResult = {
  isValid: boolean
  entityName?: string
  status?: string
  message?: string
  isStatusRetrieved?: boolean
}

export const ABNInput = ({ defaultValue, onInputChange, onAbnSubmit, inputError, ...props }: ABNInputProps) => {
  const [value, setValue] = useState<string>(defaultValue ?? '')

  const {
    data: abnResponse,
    error: abnError,
    isLoading: isLoading,
  } = useQuery({
    queryKey: ['abn', value],
    queryFn: () => fetchAbnDetails(value),
    enabled: !!value && value.length === 11,
    retry: false,
    select: (data): AbnLookupResult => ({
      isValid: data.AbnStatus === 'Active' && !!data.Abn,
      entityName: data.EntityName,
      status: data.AbnStatus,
      message: data.Message,
      isStatusRetrieved: !!data.AbnStatus,
    }),
  })
  const error = abnResponse?.status !== 'Active' ? (abnResponse?.message ?? abnError?.message) : null
  const isValidABN = abnResponse?.status === 'Active' && !!abnResponse?.isValid && !error

  useEffect(() => {
    if (onAbnSubmit) {
      onAbnSubmit({ abn: value, isValidABN: isValidABN, error: error })
    }
  }, [isValidABN, error])

  return (
    <div>
      <div className="relative z-10">
        <Input
          value={value}
          onChange={(e) => {
            const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 11)
            setValue(digitsOnly)
            onInputChange?.(digitsOnly)
          }}
          id="abn-input"
          className={cn('overflow-scroll', props.className, !!error && 'border-error-700')}
          error={inputError ?? undefined}
          type="text"
          {...props}
        />
        {isLoading && (
          <div className="absolute right-4 top-3.5">
            <Loader type={'default'} />
          </div>
        )}
      </div>
      {!!error && <p className="mt-2 text-sm font-medium text-error-700">{error ?? 'Error fetching ABN details. Please check the ABN and try again.'}</p>}
      <div>
        {!!abnResponse?.isStatusRetrieved && (
          <div className="mt-2 flex flex-col gap-4">
            <p className="text-sm font-medium text-neutral-900">The number entered matches the following business</p>
            <div className={cn('flex flex-col gap-2 rounded-lg border-2  p-4', abnResponse?.isValid ? 'border-success-700' : 'border-error-700')}>
              <p className="text-base font-medium uppercase text-neutral-900">{abnResponse?.entityName}</p>
              <p className="text-sm text-neutral-700">ABN Status: {abnResponse?.status}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
